/**
 * OlogyContent - Displays individual ology content in a tab
 */
export default class OlogyContent {
  constructor(container, slug) {
    this.container = container;
    this.slug = slug;
  }

  async render() {
    this.container.innerHTML = `
      <div class="ology-content">
        <div class="ology-loading">
          <p>Loading ology...</p>
        </div>
      </div>
    `;

    try {
      // Fetch content from the Astro server
      const content = await this.fetchOlogyContent(this.slug);
      
      if (content) {
        this.renderContent(content);
      } else {
        throw new Error('Ology not found');
      }
      
    } catch (error) {
      console.error('Error loading ology:', error);
      this.showError('Failed to load ology content');
    }
  }

  async fetchOlogyContent(slug) {
    try {
      // Determine the correct API URL
      let apiUrl;
      if (window.location.hostname === 'localhost') {
        // Development: Use the local Astro dev server
        apiUrl = `http://localhost:4321/api/ologies/${slug}.json`;
      } else {
        // Production: Use the actual build location
        apiUrl = `/file/ologies/api/ologies/${slug}.json`;
      }
      
      // Try to fetch from the API endpoint
      const response = await fetch(apiUrl);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.error) {
          throw new Error(data.error);
        }
        
        // Convert markdown content to HTML (basic conversion)
        const htmlContent = this.markdownToHtml(data.content);
        
        return {
          title: data.title,
          content: htmlContent,
          metadata: {
            description: data.description,
            type: data.type,
            publishDate: data.publishDate,
            tags: data.tags,
            featured: data.featured,
            author: data.author,
            duration: data.duration,
            difficulty: data.difficulty,
            prerequisites: data.prerequisites,
            relatedContent: data.relatedContent
          },
          // Rich media content
          video: data.video,
          charts: data.charts || [],
          tables: data.tables || [],
          gallery: data.gallery
        };
      }
      
      // If API is not available, throw error
      throw new Error(`API not available at ${apiUrl}`);
      
    } catch (error) {
      console.error('Failed to fetch ology:', error);
      throw error;
    }
  }

  // Basic markdown to HTML conversion
  markdownToHtml(markdown) {
    if (!markdown) return '';
    
    let html = markdown
      // Headers
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      // Bold
      .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.*)\*/gim, '<em>$1</em>')
      // Code blocks
      .replace(/```([\s\S]*?)```/gim, '<pre><code>$1</code></pre>')
      // Inline code
      .replace(/`([^`]*)`/gim, '<code>$1</code>')
      // Links
      .replace(/\[([^\]]*)\]\(([^\)]*)\)/gim, '<a href="$2">$1</a>')
      // Line breaks
      .replace(/\n\n/gim, '</p><p>')
      .replace(/\n/gim, '<br>');
    
    // Wrap in paragraphs
    html = '<p>' + html + '</p>';
    
    // Clean up empty paragraphs
    html = html.replace(/<p><\/p>/gim, '');
    html = html.replace(/<p><h/gim, '<h');
    html = html.replace(/<\/h([1-6])><\/p>/gim, '</h$1>');
    html = html.replace(/<p><pre>/gim, '<pre>');
    html = html.replace(/<\/pre><\/p>/gim, '</pre>');
    
    return html;
  }

  // Process inline chart shortcodes in content
  processInlineCharts(html, charts) {
    if (!charts || charts.length === 0) return html;
    
    // Replace [chart:title] shortcodes with chart containers
    return html.replace(/\[chart:([^\]]+)\]/g, (match, chartTitle) => {
      // Find chart by title (case-insensitive)
      const chart = charts.find(c => 
        c.title && c.title.toLowerCase().includes(chartTitle.toLowerCase())
      );
      
      if (chart) {
        const chartIndex = charts.indexOf(chart);
        const chartId = `chart-${this.slug}-${chartIndex}`;  // Use same ID pattern as regular charts
        return `
          <div class="inline-chart-container">
            <div class="chart-container">
              <h3 class="chart-title">${chart.title}</h3>
              ${chart.description ? `<p class="chart-description">${chart.description}</p>` : ''}
              <div class="chart-wrapper">
                <canvas 
                  id="${chartId}" 
                  width="400" 
                  height="300"
                  style="max-width: 100%; height: 300px;"
                ></canvas>
              </div>
            </div>
          </div>
        `;
      }
      return match; // Return original if chart not found
    });
  }

  renderContent(content) {
    // Process inline charts in the content first
    let processedContent = this.processInlineCharts(content.content, content.charts);
    
    // Build the rich media sections (only for charts not used inline)
    let richMediaHTML = '';
    
    // Video section
    if (content.video) {
      richMediaHTML += this.renderVideo(content.video);
    }
    
    // Charts section - only show charts that aren't used inline
    const inlineChartTitles = this.getInlineChartTitles(content.content);
    const nonInlineCharts = content.charts ? content.charts.filter(chart => 
      !inlineChartTitles.some(title => 
        chart.title && chart.title.toLowerCase().includes(title.toLowerCase())
      )
    ) : [];
    
    if (nonInlineCharts.length > 0) {
      richMediaHTML += `
        <div class="charts-section">
          <h2>Data Visualizations</h2>
          ${this.renderCharts(nonInlineCharts)}
        </div>
      `;
    }
    
    // Tables section
    if (content.tables && content.tables.length > 0) {
      richMediaHTML += `
        <div class="tables-section">
          <h2>Data Tables</h2>
          ${content.tables.map((table, index) => this.renderTable(table, index)).join('')}
        </div>
      `;
    }
    
    // Related content section
    if (content.metadata.relatedContent && content.metadata.relatedContent.length > 0) {
      richMediaHTML += `
        <div class="related-content">
          <h2>Related Content</h2>
          <div class="related-links">
            ${content.metadata.relatedContent.map(slug => `
              <a href="/file/ologies/${slug}" class="related-link">
                ${slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </a>
            `).join('')}
          </div>
        </div>
      `;
    }
    
    // Build header with metadata
    const headerHTML = this.renderOlogyHeader(content);
    
    this.container.innerHTML = `
      <div class="ology-content">
        <div class="ology-content__wrapper">
          ${headerHTML}
          <div class="ology-body">
            ${processedContent}
          </div>
          ${richMediaHTML}
        </div>
      </div>
    `;
    
    // Initialize interactive components (both inline and section charts)
    this.initializeAllCharts(content.charts || []);
    this.initializeTables(content.tables || []);
    
    // Process any links in the content to prevent navigation
    this.processLinks();
  }

  // Helper method to extract chart titles from inline shortcodes
  getInlineChartTitles(content) {
    const matches = content.match(/\[chart:([^\]]+)\]/g) || [];
    return matches.map(match => match.replace(/\[chart:([^\]]+)\]/, '$1'));
  }

  // Render ology header with metadata
  renderOlogyHeader(content) {
    const publishDate = content.metadata.publishDate ? new Date(content.metadata.publishDate) : null;
    const formattedDate = publishDate ? publishDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }) : null;

    return `
      <header class="ology-header">
        <h1 class="ology-title">${content.title}</h1>
        <p class="ology-description">${content.metadata.description}</p>
        
        <div class="ology-details">
          ${content.metadata.author && formattedDate ? `
            <span class="ology-byline">
              Written by ${content.metadata.author} and last updated on ${formattedDate}
            </span>
          ` : ''}
        </div>
      </header>
    `;
  }

  renderVideo(video) {
    const embedUrl = this.getVideoEmbedUrl(video);
    
    return `
      <div class="video-container" data-platform="${video.platform}">
        ${video.isLive ? `
          <div class="live-indicator">
            <span class="live-dot"></span>
            LIVE
          </div>
        ` : ''}
        
        <div class="video-wrapper">
          <iframe
            src="${embedUrl}"
            title="Video content"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            loading="lazy"
          ></iframe>
        </div>
        
        ${video.duration ? `
          <div class="video-meta">
            <span class="duration">${video.duration}</span>
            <span class="platform-badge">${video.platform}</span>
          </div>
        ` : ''}
      </div>
    `;
  }

  getVideoEmbedUrl(video) {
    switch (video.platform) {
      case 'youtube':
        return `https://www.youtube.com/embed/${video.embedId}`;
      case 'vimeo':
        return `https://player.vimeo.com/video/${video.embedId}`;
      case 'twitch':
        return `https://player.twitch.tv/?video=${video.embedId}&parent=localhost`;
      default:
        return video.url;
    }
  }

  renderCharts(charts) {
    return charts.map((chart, index) => {
      const chartId = `chart-${this.slug}-${index}`;
      return `
        <div class="chart-container">
          <h3 class="chart-title">${chart.title}</h3>
          ${chart.description ? `<p class="chart-description">${chart.description}</p>` : ''}
          <div class="chart-wrapper">
            <canvas 
              id="${chartId}" 
              width="400" 
              height="300"
              style="max-width: 100%; height: 300px;"
            ></canvas>
          </div>
        </div>
      `;
    }).join('');
  }

  renderTable(table, index) {
    const tableId = `table-${this.slug}-${index}`;
    
    return `
      <div class="table-container">
        ${table.caption ? `<div class="table-caption">${table.caption}</div>` : ''}
        
        ${table.filterable ? `
          <div class="table-controls">
            <input 
              type="text" 
              placeholder="Filter table..." 
              class="table-filter"
              data-table="${tableId}"
            />
          </div>
        ` : ''}
        
        <div class="table-wrapper">
          <table id="${tableId}" class="enhanced-table" data-sortable="${table.sortable}">
            <thead>
              <tr>
                ${table.headers.map((header, index) => `
                  <th data-column="${index}" class="${table.sortable ? 'sortable' : ''}">
                    ${header}
                    ${table.sortable ? '<span class="sort-indicator"></span>' : ''}
                  </th>
                `).join('')}
              </tr>
            </thead>
            <tbody>
              ${table.rows.map(row => `
                <tr>
                  ${row.map(cell => `<td>${cell}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  async initializeAllCharts(charts) {
    if (charts.length === 0) return;
    
    console.log('Starting chart initialization with charts:', charts);
    
    // Check if Chart.js is already loaded
    if (window.Chart) {
      console.log('Chart.js already available globally');
      this.initializeChartsWithLibrary(charts, window.Chart);
      return;
    }
    
    // Load Chart.js via script tag
    console.log('Loading Chart.js via script tag...');
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/chart.js@3.9.1/dist/chart.min.js';
    script.onload = () => {
      console.log('Chart.js loaded successfully, Chart available:', !!window.Chart);
      if (window.Chart) {
        this.initializeChartsWithLibrary(charts, window.Chart);
      } else {
        console.error('Chart.js loaded but Chart constructor not available');
        this.showChartErrors(charts, 'Chart.js loaded but constructor not available');
      }
    };
    script.onerror = (error) => {
      console.error('Failed to load Chart.js:', error);
      this.showChartErrors(charts, 'Failed to load Chart.js library');
    };
    
    // Add script to head
    document.head.appendChild(script);
  }

  // Legacy method for backward compatibility
  async initializeCharts(charts) {
    return this.initializeAllCharts(charts);
  }

  initializeChartsWithLibrary(charts, Chart) {
    // Small delay to ensure DOM elements are ready
    setTimeout(() => {
      charts.forEach((chart, index) => {
        const chartId = `chart-${this.slug}-${index}`;
        const ctx = document.getElementById(chartId);
        
        if (!ctx) {
          console.log(`Chart canvas element not found: ${chartId}`);
          return;
        }
        
        console.log(`Initializing chart: ${chartId}`, chart);
        console.log('Chart constructor type:', typeof Chart);
        console.log('Chart constructor:', Chart);
        
        try {
          const chartInstance = new Chart(ctx, {
            type: chart.type === 'area' ? 'line' : chart.type,
            data: chart.data,
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  labels: {
                    color: '#d4d4d4',
                    font: {
                      family: 'Fira Mono, monospace'
                    }
                  }
                },
                tooltip: {
                  backgroundColor: '#252526',
                  titleColor: '#39ff14',
                  bodyColor: '#d4d4d4',
                  borderColor: '#444',
                  borderWidth: 1
                }
              },
              scales: chart.type !== 'pie' && chart.type !== 'doughnut' ? {
                x: {
                  ticks: { 
                    color: '#d4d4d4',
                    font: {
                      family: 'Fira Mono, monospace'
                    }
                  },
                  grid: { color: '#444' }
                },
                y: {
                  ticks: { 
                    color: '#d4d4d4',
                    font: {
                      family: 'Fira Mono, monospace'
                    }
                  },
                  grid: { color: '#444' },
                  beginAtZero: true
                }
              } : {},
              // Apply area fill for area charts
              ...(chart.type === 'area' ? {
                fill: true,
                elements: {
                  line: {
                    fill: 'origin'
                  }
                }
              } : {}),
              // Merge any custom config
              ...(chart.config || {})
            }
          });
          
          console.log(`Chart ${chartId} initialized successfully:`, chartInstance);
        } catch (chartError) {
          console.error(`Failed to initialize chart ${chartId}:`, chartError);
          this.showSingleChartError(chartId, chart.title, chartError.message);
        }
      });
    }, 100);
  }

  showChartErrors(charts, message) {
    charts.forEach((chart, index) => {
      const chartId = `chart-${this.slug}-${index}`;
      this.showSingleChartError(chartId, chart.title, message);
    });
  }

  showSingleChartError(chartId, title, message) {
    const ctx = document.getElementById(chartId);
    if (ctx) {
      const container = ctx.closest('.chart-container');
      if (container) {
        container.innerHTML = `
          <div class="chart-error">
            <h3 class="chart-title">${title || 'Chart'}</h3>
            <p style="color: #ff6b6b; text-align: center; padding: 2rem;">
              ${message}
            </p>
          </div>
        `;
      }
    }
  }

  initializeTables(tables) {
    if (tables.length === 0) return;
    
    // Add sorting functionality
    document.querySelectorAll('.sortable').forEach(header => {
      header.addEventListener('click', function() {
        const table = this.closest('table');
        const column = parseInt(this.dataset.column);
        const tbody = table.querySelector('tbody');
        const rows = Array.from(tbody.querySelectorAll('tr'));
        
        const isAscending = this.classList.contains('sort-asc');
        
        // Clear all sort classes
        table.querySelectorAll('th').forEach(th => {
          th.classList.remove('sort-asc', 'sort-desc');
        });
        
        // Sort rows
        rows.sort((a, b) => {
          const aVal = a.cells[column].textContent.trim();
          const bVal = b.cells[column].textContent.trim();
          
          // Try numeric comparison first
          const aNum = parseFloat(aVal);
          const bNum = parseFloat(bVal);
          
          if (!isNaN(aNum) && !isNaN(bNum)) {
            return isAscending ? bNum - aNum : aNum - bNum;
          }
          
          // String comparison
          return isAscending ? 
            bVal.localeCompare(aVal) : 
            aVal.localeCompare(bVal);
        });
        
        // Update DOM
        rows.forEach(row => tbody.appendChild(row));
        
        // Update sort indicator
        this.classList.add(isAscending ? 'sort-desc' : 'sort-asc');
      });
    });
    
    // Add filter functionality
    document.querySelectorAll('.table-filter').forEach(filter => {
      filter.addEventListener('input', function() {
        const tableId = this.dataset.table;
        const table = document.getElementById(tableId);
        const rows = table.querySelectorAll('tbody tr');
        const searchTerm = this.value.toLowerCase();
        
        rows.forEach(row => {
          const text = row.textContent.toLowerCase();
          row.style.display = text.includes(searchTerm) ? '' : 'none';
        });
      });
    });
  }

  processLinks() {
    // Find all links in the content and handle them appropriately
    const links = this.container.querySelectorAll('a');
    
    links.forEach(link => {
      const href = link.getAttribute('href');
      
      if (href && href.startsWith('/file/ologies/')) {
        // This is a link to another ology
        link.addEventListener('click', (event) => {
          event.preventDefault();
          
          const slug = href.replace('/file/ologies/', '');
          const title = link.textContent || slug;
          
          // Open in a new tab
          const openEvent = new CustomEvent('file:open', { 
            detail: { 
              filepath: `ologies/${slug}`,
              title: title
            },
            bubbles: true 
          });
          document.dispatchEvent(openEvent);
        });
      } else if (href && href.startsWith('/file/')) {
        // This is a link to another file in the main site
        link.addEventListener('click', (event) => {
          event.preventDefault();
          
          const filepath = href.replace('/file/', '');
          const title = link.textContent || filepath.split('/').pop();
          
          // Open in a new tab
          const openEvent = new CustomEvent('file:open', { 
            detail: { 
              filepath: filepath,
              title: title
            },
            bubbles: true 
          });
          document.dispatchEvent(openEvent);
        });
      }
      // External links will work normally
    });
  }

  showError(message) {
    const isLocalhost = window.location.hostname === 'localhost';
    
    this.container.innerHTML = `
      <div class="ology-content">
        <div class="ology-error">
          <h2>Cannot Load Ology: ${this.slug}</h2>
          <p>${message}</p>
          ${isLocalhost ? `
            <p><strong>Development Setup:</strong></p>
            <ol>
              <li>Make sure the ologies server is running: <code>cd ologies-content && npm run dev</code></li>
              <li>Check that the ology exists at <code>ologies-content/src/content/ologies/${this.slug}.mdx</code></li>
              <li>Verify the server is accessible at <a href="http://localhost:4321/api/ologies/${this.slug}.json" target="_blank">http://localhost:4321/api/ologies/${this.slug}.json</a></li>
            </ol>
          ` : `
            <p><strong>This appears to be a deployment issue.</strong></p>
            <p>The ology content should be available at the API endpoint.</p>
            <p>Expected location: <code>/api/ologies/${this.slug}.json</code></p>
          `}
        </div>
      </div>
    `;
  }
} 